/* What is under the finger.
 *
 * ═══ RAYMARINE'S MODEL, WHICH IS THE RIGHT ONE ═══
 *
 * A long press does not ACT — it ASKS. It lists everything within a short
 * radius of the point and lets you choose. That turns one gesture into the way
 * you interrogate the whole chart, instead of every layer inventing its own tap
 * handling and half of them never getting one. Today the AIS targets, the wind
 * barbs, the tide stations and the current arrows are all drawn and NONE of
 * them can be tapped.
 *
 * ═══ SCREEN PIXELS, NOT METRES ═══
 *
 * The radius is in pixels because the finger is a fixed size and the chart is
 * not. At zoom 9 a 40 m radius is a third of a pixel and nothing is ever
 * touchable; at zoom 18 it is half the screen and everything is. 14 px is the
 * route editor's own target, arrived at there for the same reason — a wet
 * finger on a moving boat — so the two agree by construction.
 *
 * ═══ ORDERED BY DISTANCE, THEN BY WHAT MATTERS ═══
 *
 * Distance first, because the thing you pressed on is the thing you meant. But
 * a tie is broken by KIND, and the order is a safety judgement rather than a
 * preference: a ship that could hit you outranks a tide station that cannot.
 */

export type PickKind =
  | 'vessel'
  | 'ais'
  | 'waypoint'
  | 'route'
  | 'current'
  | 'tide'
  | 'wind'
  | 'zone'
  | 'fishing'
  | 'rockfish'
  | 'anchor'
  | 'mark'
  | 'track'
  | 'note';

/** Lower sorts first when two things are the same distance away. */
const RANK: Record<PickKind, number> = {
  ais: 0, // it moves, and it can hit you
  vessel: 1,
  anchor: 2,
  mark: 3,
  waypoint: 4,
  route: 5,
  current: 6, // affects where you end up
  tide: 7,
  wind: 8,
  zone: 9, // a forecast, not a thing — below the wind it describes
  /* A Rockfish Conservation Area is a CLOSURE, not an advisory: inside one you
     may not fish for finfish at all, whatever the species glyphs say. So it
     outranks the per-species rules it overrides. */
  rockfish: 10,
  fishing: 11, // a management/regulation note, one rung below a forecast
  track: 12, // where you have already been: the least urgent thing on the chart
  note: 13 // somebody else's opinion about the water — least urgent of all
};

export interface Candidate {
  kind: PickKind;
  /** Stable identity, so a sheet can be reopened after the list rebuilds. */
  id: string;
  label: string;
  /** A short second line — range, rate, speed. Optional. */
  detail?: string;
  lat: number;
  lon: number;
  /**
   * For things that are LINES rather than points — a route, the track.
   *
   * Measuring those from a single position would be nonsense: a route's
   * midpoint may be twenty miles from the leg you actually pressed on, so it
   * would be unpickable everywhere except one spot and would report a range to
   * somewhere you were not asking about. With a path, distance is to the
   * NEAREST POINT ON THE LINE, and the hit reports that point — which is the
   * position the range, the bearing and the sheet should all be about.
   */
  path?: readonly { lat: number; lon: number }[];
}

export interface Hit extends Candidate {
  /** Screen pixels from the press point. */
  dist: number;
}

/** Project a geographic point to screen pixels. MapLibre's `map.project`. */
export type Project = (lonLat: [number, number]) => { x: number; y: number };

/* Deliberately generous compared with the editor's 14 px. A vertex drag has to
 * be precise or it grabs the wrong point; this only has to gather things up,
 * and the list that comes back is chosen from by NAME. Missing the ship you
 * meant costs a second press; catching one extra costs a glance. */
export const PICK_RADIUS_PX = 34;

/** The most rows a menu can carry before it stops being readable at a glance. */
export const MAX_HITS = 12;

/**
 * Everything within `radius` screen pixels of `pt`, nearest first.
 *
 * Candidates are gathered by the caller because only the page knows which
 * layers are switched on — an object the user has hidden must not be pickable,
 * or the menu answers for a chart they are not looking at.
 */
export function pick(
  pt: { x: number; y: number },
  candidates: readonly Candidate[],
  project: Project,
  radius = PICK_RADIUS_PX,
  max = MAX_HITS
): Hit[] {
  const hits: Hit[] = [];
  for (const c of candidates) {
    const near = c.path && c.path.length >= 2 ? nearestOnPath(pt, c.path, project) : null;
    if (near) {
      if (near.dist <= radius) hits.push({ ...c, ...near.at, dist: near.dist });
      continue;
    }

    if (!Number.isFinite(c.lat) || !Number.isFinite(c.lon)) continue;
    let p: { x: number; y: number };
    try {
      p = project([c.lon, c.lat]);
    } catch {
      // Off the projectable world, which happens near the poles and when the
      // map is mid-rebuild. Not a reason to lose the rest of the list.
      continue;
    }
    if (!Number.isFinite(p.x) || !Number.isFinite(p.y)) continue;
    const dist = Math.hypot(p.x - pt.x, p.y - pt.y);
    if (dist <= radius) hits.push({ ...c, dist });
  }

  hits.sort((a, b) => {
    /* Within a couple of pixels two things are, for a finger, in the same
     * place — so rank decides rather than a sub-pixel difference nobody could
     * have aimed at. */
    if (Math.abs(a.dist - b.dist) > 2) return a.dist - b.dist;
    return RANK[a.kind] - RANK[b.kind];
  });
  return hits.slice(0, max);
}

/**
 * Nearest point on a polyline, in screen pixels.
 *
 * Segments are walked in pixel space rather than in degrees for the reason the
 * whole module works that way: the tolerance is a finger. A point whose
 * projection fails or comes back non-finite BREAKS THE CHAIN rather than being
 * skipped — joining the segment either side of it would draw a shortcut across
 * a gap the line does not have, and hit-test against water the route never
 * crosses. A track that crosses the antimeridian is exactly this case.
 */
function nearestOnPath(
  pt: { x: number; y: number },
  path: readonly { lat: number; lon: number }[],
  project: Project
): { dist: number; at: { lat: number; lon: number } } | null {
  let best: { dist: number; at: { lat: number; lon: number } } | null = null;
  let prev: { x: number; y: number; lat: number; lon: number } | null = null;

  for (const q of path) {
    let cur: { x: number; y: number; lat: number; lon: number } | null = null;
    if (Number.isFinite(q.lat) && Number.isFinite(q.lon)) {
      try {
        const p = project([q.lon, q.lat]);
        if (Number.isFinite(p.x) && Number.isFinite(p.y)) {
          cur = { x: p.x, y: p.y, lat: q.lat, lon: q.lon };
        }
      } catch {
        cur = null;
      }
    }

    if (prev && cur) {
      const vx = cur.x - prev.x;
      const vy = cur.y - prev.y;
      const len2 = vx * vx + vy * vy;
      // A zero-length segment has no interior; its endpoint is covered anyway
      // by the neighbouring segments, so skipping avoids a divide by zero.
      if (len2 > 0) {
        let t = ((pt.x - prev.x) * vx + (pt.y - prev.y) * vy) / len2;
        t = Math.max(0, Math.min(1, t));
        const d = Math.hypot(prev.x + t * vx - pt.x, prev.y + t * vy - pt.y);
        if (!best || d < best.dist) {
          best = {
            dist: d,
            at: {
              lat: prev.lat + t * (cur.lat - prev.lat),
              lon: prev.lon + t * (cur.lon - prev.lon)
            }
          };
        }
      }
    }
    prev = cur;
  }
  return best;
}

/** Distance in metres between two positions — the menu header's range. */
export function rangeM(
  a: { lat: number; lon: number },
  b: { lat: number; lon: number }
): number {
  const dLat = (b.lat - a.lat) * 111_320;
  const dLon = (b.lon - a.lon) * 111_320 * Math.cos((a.lat * Math.PI) / 180);
  return Math.hypot(dLat, dLon);
}

/** True bearing in degrees from `a` to `b`. */
export function bearingDeg(
  a: { lat: number; lon: number },
  b: { lat: number; lon: number }
): number {
  const dLat = (b.lat - a.lat) * 111_320;
  const dLon = (b.lon - a.lon) * 111_320 * Math.cos((a.lat * Math.PI) / 180);
  return (((Math.atan2(dLon, dLat) * 180) / Math.PI) + 360) % 360;
}
